import { isDefined, useDictionary } from '@eih-netbroker/common';
import { useEffect, useMemo, useState } from 'react';
import { useLocale } from '../../../../context/NbLocalizationContext';
import { CodeInputModal, CodeInputModalLabels } from '../../../../shared/components/code-input-modal/CodeInputModal';
import { ModalHeader } from '../../../../shared/components/modal/ModalHeader';
import { NbTabs, TabItem } from '../../../../shared/components/tabs/Tabs';
import { useDeposit } from '../../services/useDeposit';
import { useDepositInternal } from '../../services/useDepositInternal';
import { CashActionStep } from '../../states/cashActionAtoms';
import { CashActionReceivedMessages } from '../common/CashActionReceivedMessages';
import { CashActionSummary } from '../common/CashActionSummary';
import { CashDepositExternalTabContent } from './CashDepositExternalTabContent';
import { CashDepositInternalTabContent } from './CashDepositInternalTabContent';
import classes from './CashDepositModal.module.scss';

type CashDepositModalProps = {
  onClose: () => void;
};

enum InternalDepositTab {
  'fromErste' = 0,
  'fromOutside' = 1,
}

export const CashDepositModal = ({ onClose }: CashDepositModalProps) => {
  const { translation } = useLocale();
  const [selectedTab, setSelectedTab] = useState<InternalDepositTab>(0);
  const { refreshDepositDetails, handleModalClose, handleTwoFactorRestart } = useDeposit();
  const {
    handleDepositSubmit,
    handleDepositConfirm,
    smsCodeError,
    canRestartTwoFactor,
    setCashActionStep,
    cashActionStep,
    cashActionTemp,
  } = useDepositInternal();
  const { formatDictionaryType } = useDictionary();

  const tabs = useMemo<TabItem[]>(
    () => [
      { id: InternalDepositTab.fromErste.toString(), label: translation.cashAction.deposit.tabs.fromErste },
      { id: InternalDepositTab.fromOutside.toString(), label: translation.cashAction.deposit.tabs.fromOutside },
    ],
    [translation],
  );

  useEffect(() => {
    refreshDepositDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init effect
  }, []);

  useEffect(() => {
    if (cashActionStep === CashActionStep.Summary && !cashActionTemp) {
      setCashActionStep(CashActionStep.Params);
    }
  }, [cashActionStep, cashActionTemp, setCashActionStep]);

  const codeInputModalLabels = useMemo<CodeInputModalLabels>(
    () => ({
      title: translation.cashAction.confirm.deposit.title,
      description: translation.cashAction.confirm.description,
      inputLabel: translation.cashAction.confirm.smsCodeLabel,
      cancelLabel: translation.general.cancel,
      submitLabel: translation.cashAction.confirm.deposit.submit,
    }),
    [translation],
  );

  if (cashActionStep === CashActionStep.ReceivedMessages) {
    return (
      <CashActionReceivedMessages
        title={translation.cashAction.deposit.title}
        onClose={() => handleModalClose(onClose)}
      />
    );
  }

  if (cashActionStep === CashActionStep.Confirm) {
    return (
      <CodeInputModal
        error={smsCodeError}
        isInitializing={false}
        onSubmit={async (code: string) => handleDepositConfirm(code, () => handleModalClose(onClose))}
        onCancel={() => handleModalClose(onClose)}
        onClose={() => handleModalClose(onClose)}
        onRestart={handleTwoFactorRestart}
        canRestart={canRestartTwoFactor}
        labels={codeInputModalLabels}
      />
    );
  }

  if (cashActionStep === CashActionStep.Summary && isDefined(cashActionTemp)) {
    return (
      <CashActionSummary
        title={translation.cashAction.summary.deposit.title}
        submitButtonText={translation.cashAction.summary.deposit.submit}
        amountLabel={translation.cashAction.summary.deposit.amountLabel}
        onClose={() => handleModalClose(onClose)}
        onBack={() => setCashActionStep(CashActionStep.Params)}
        onSubmit={() => handleDepositSubmit(() => handleModalClose(onClose))}
        sourceAccountText={cashActionTemp.sourceAccountNumber}
        targetAccountText={`${formatDictionaryType(
          cashActionTemp.targetAccountCategory ?? '',
          'accountCategoryTypes',
        )} - ${cashActionTemp.targetClientId}`}
      />
    );
  }

  return (
    <div className={classes.container}>
      <ModalHeader
        title={translation.cashAction.deposit.title}
        onClose={() => handleModalClose(onClose)}
        isSmall
        isWithDivider
        className={classes.header}
      />
      <NbTabs
        tabs={tabs}
        selected={selectedTab}
        onTabChange={index => setSelectedTab(index)}
        className={classes.tabs}
      />
      <div className={classes.tabsDivider} />
      {selectedTab === InternalDepositTab.fromErste && <CashDepositInternalTabContent />}
      {selectedTab === InternalDepositTab.fromOutside && <CashDepositExternalTabContent />}
    </div>
  );
};
